import React, { useEffect } from "react";
import {
  AuthBindings,
  Authenticated,
  Refine,
} from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  ThemedLayoutV2,
  ThemedSiderV2,
  useNotificationProvider,
} from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "./styles/sidebar.css";
import { RoleProvider, useRoles } from "./contexts/role-context";
import { OrganizationProvider, useOrganization } from "./contexts/organization-context";
import { SubscriptionProvider } from "./contexts/subscription-context";
import { useAuth0 } from "@auth0/auth0-react";
import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import dataProvider from "@refinedev/simple-rest";
import { App as AntdApp, ConfigProvider, Button, Typography } from "antd";
import { RefineThemes } from "@refinedev/antd";
import axios from "axios";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { Header } from "./components/header";
import { Login } from "./pages/login";
import { Profile } from "./pages/Profile";
import { EmailVerification } from "./pages/EmailVerification";
import { PromptEditor } from "./pages/admin/PromptEditor";
import { PDPMPromptEditor } from "./pages/admin/PDPMPromptEditor";
import { VectorEditor } from "./pages/admin/VectorEditor";
import { CodeIdentifierUser } from "./pages/CodeIdentifierUser";
import { CodeIdentifier } from "./pages/admin/CodeIdentifier";
import { CacheVersion } from "./pages/admin/CacheVersion";
import { UserActivityReport } from "./pages/admin/UserActivityReport";
import { OrganizationAdmin } from "./pages/admin/OrganizationAdmin";
import { ICD10Categories, ICD10NTAPoints, ICD10SLP, ICD10CategoriesViewer, ICD10NTAPointsViewer, ICD10SLPViewer } from "./pages/admin";
import TrialUserManagement from "./pages/admin/TrialUserManagement";
import { Organization } from "./pages/organization/Organization";
import { Subscription } from "./pages/organization/Subscription";
import { Facilities } from "./pages/organization/Facilities";
import { Invoices } from "./pages/organization/Invoices";
import { AccessControl } from "./components/AccessControl";
import { EmailVerificationControl } from "./components/EmailVerificationControl";
import type { IResourceItem, Action } from "@refinedev/core";
import { AuthCallback } from "./pages/auth-callback";
import {
  BuildOutlined,
  BankOutlined,
  TeamOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  SettingOutlined,
  EditOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  BarChartOutlined,
  UserOutlined,
  LineChartOutlined,
  MedicineBoxOutlined,
  FolderOutlined,
  ExperimentOutlined,
  CustomerServiceOutlined,
} from "@ant-design/icons";

// Declare global constants injected by Vite define
declare const BASE_URL: string;
declare const API_BASE_URL: string;

const customTitleHandler = ({ autoGeneratedTitle }: { autoGeneratedTitle: string; resource?: IResourceItem; action?: Action; params?: Record<string, string | undefined>; pathname?: string }) => {
    // Remove any existing suffix and add | CodePilot
    return autoGeneratedTitle.replace(/\s\|.*/, "") + " | CodePilot";
};

function isAuthCallbackWithError(): boolean {
  if (typeof window === "undefined") return false;
  const pathname = window.location.pathname;
  const error = new URLSearchParams(window.location.search).get("error");
  return pathname === "/auth/callback" && Boolean(error);
}

function AppContent() {
  const { isLoading: auth0Loading, isAuthenticated, user, logout, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { roles, loading: rolesLoading, hasUserRole } = useRoles();
  const { isOrganizationOwner, ownedOrganizations, loading: orgLoading } = useOrganization();
  const [loginLogged, setLoginLogged] = React.useState(false);
  const [callbackError, setCallbackError] = React.useState(isAuthCallbackWithError());

  useEffect(() => {
    setCallbackError(isAuthCallbackWithError());
  }, [auth0Loading]);

  useEffect(() => {
    if (callbackError) return;
    if (!auth0Loading && !isAuthenticated) {
      loginWithRedirect({
        authorizationParams: {
          prompt: "select_account"
        }
      });
    }
  }, [auth0Loading, isAuthenticated, loginWithRedirect, callbackError]);

  // Log organization owner status changes
  useEffect(() => {
    console.log("[App] Organization status changed:", {
      isOrganizationOwner,
      ownedOrganizationsCount: ownedOrganizations.length,
      orgLoading
    });
  }, [isOrganizationOwner, ownedOrganizations.length, orgLoading]);

  if (callbackError) {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const errorDescription = params.get("error_description") ?? "Unknown error";
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: "40px auto", textAlign: "center" }}>
        <Typography.Title level={4}>Authentication failed</Typography.Title>
        <Typography.Paragraph style={{ color: "#666", marginBottom: 16 }}>
          {decodeURIComponent(errorDescription)}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
          Often this means the Auth0 API identifier does not match. In Auth0 Dashboard → Applications → APIs, ensure an API exists with the identifier set in VITE_AUTH0_AUDIENCE (e.g. https://code-guru-ui-api or https://codepilot-ui-api).
        </Typography.Paragraph>
        <Button type="primary" href={window.location.origin}>
          Back to login
        </Button>
      </div>
    );
  }

  if (auth0Loading || (!isAuthenticated && !user)) {
    return <span>Loading app...</span>; // Optional: add spinner here
  }

  if (!user) {
    return <span>User not authenticated. Please log in.</span>;
  }

  if (rolesLoading || orgLoading) {
    return <span>Loading roles...</span>;
  }

  const authProvider: AuthBindings = {
    login: async () => {
      return {
        success: true,
      };
    },
    logout: async () => {
      logout( {logoutParams: { returnTo: window.location.origin }});
      return {
        success: true,
      };
    },
    onError: async (error) => {
      console.error(error);
      return { error };
    },
    check: async () => {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          axios.defaults.headers.common = {
            Authorization: `Bearer ${token}`,
          };
          return {
            authenticated: true,
          };
        } else {
          return {
            authenticated: false,
            error: {
              message: "Check failed",
              name: "Token not found",
            },
            redirectTo: "/login",
            logout: true,
          };
        }
      } catch (error: unknown) {
        let errorMessage = "An unknown error occurred.";
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        return {
          authenticated: false,
          error: new Error(errorMessage),
          redirectTo: "/login",
          logout: true,
        };
      }
    },
    getPermissions: async () => null,
    getIdentity: async () => {
      if (user) {
        return {
          ...user,
          avatar: user.picture,
        };
      }
      return null;
    },
  };

  const buildResources = () => {
    console.log("[App] Building resources - isOrganizationOwner:", isOrganizationOwner, "ownedOrganizations:", ownedOrganizations.length);
    
    const baseResources: Array<{
      name: string;
      list: string;
      meta: {
        label: string;
        parent?: string;
        icon?: React.ReactNode;
      };
    }> = [
      // Top-level menu item
      {
          name: "CodeIdentifierUser",
          list: "/code-identifier",
          meta: {
              label: "Code Identifier",
              icon: <BuildOutlined />,
          },
      },
    ];

    // Add Organization menu for organization owners
    if (isOrganizationOwner) {
      console.log("[App] Adding Organization menu items");
      baseResources.push(
        // Organization top-level menu
        {
            name: "OrganizationMenu",
            list: "/organization",
            meta: {
                label: "Organization",
                icon: <BankOutlined />,
            },
        },
        // Children under Organization
        {
          name: "OrganizationSettings",
          list: "/organization/settings",
          meta: {
              label: "Organization",
              parent: "OrganizationMenu",
              icon: <BankOutlined />,
          },
        },
        {
          name: "Facilities",
          list: "/organization/facilities",
          meta: {
              label: "Facilities",
              parent: "OrganizationMenu",
              icon: <TeamOutlined />,
          },
        },
        {
            name: "Subscription",
            list: "/organization/subscription",
            meta: {
                label: "Subscription",
                parent: "OrganizationMenu",
                icon: <CreditCardOutlined />,
            },
        },
        {
            name: "Invoices",
            list: "/organization/invoices",
            meta: {
                label: "Invoices",
                parent: "OrganizationMenu",
                icon: <FileTextOutlined />,
            },
        }
      );
    }

    // Add Admin menu for admins
    if (roles.includes("Admin")) {
      baseResources.push(
        // Admin top-level menu
        {
            name: "AdminMenu",
            list: "/admin",
            meta: {
                label: "Admin",
                icon: <SettingOutlined />,
            },
        },
        // Code Ident Testing submenu
        {
            name: "CodeIdentTestingMenu",
            list: "/admin/code-testing",
            meta: {
                label: "Code Ident Testing",
                parent: "AdminMenu",
                icon: <ExperimentOutlined />,
            },
        },
        {
            name: "AdminCodeIdentifier",
            list: "/admin/code-identifier",
            meta: {
                label: "Code Identifier",
                parent: "CodeIdentTestingMenu",
                icon: <DatabaseOutlined />,
            },
        },
        {
            name: "PromptEditor",
            list: "/admin/prompt-editor",
            meta: {
                label: "Prompt Editor",
                parent: "CodeIdentTestingMenu",
                icon: <EditOutlined />,
            },
        },
        {
            name: "PDPMPromptEditor",
            list: "/admin/pdpm-prompts",
            meta: {
                label: "PDPM Prompts",
                parent: "CodeIdentTestingMenu",
                icon: <MedicineBoxOutlined />,
            },
        },
        {
            name: "CacheVersion",
            list: "/admin/cache-version",
            meta: {
                label: "Cache Version",
                parent: "CodeIdentTestingMenu",
                icon: <BarChartOutlined />,
            },
        },
        // Data submenu
        {
            name: "DataMenu",
            list: "/admin/data",
            meta: {
                label: "Data",
                parent: "AdminMenu",
                icon: <FolderOutlined />,
            },
        },
        {
            name: "ICD10Categories",
            list: "/admin/data/icd10-categories",
            meta: {
                label: "ICD-10 Categories",
                parent: "DataMenu",
                icon: <MedicineBoxOutlined />,
            },
        },
        {
            name: "ICD10NTAPoints",
            list: "/admin/data/icd10-nta-points",
            meta: {
                label: "ICD-10 NTA Points",
                parent: "DataMenu",
                icon: <MedicineBoxOutlined />,
            },
        },
        {
            name: "ICD10SLP",
            list: "/admin/data/icd10-slp",
            meta: {
                label: "ICD-10 SLP",
                parent: "DataMenu",
                icon: <MedicineBoxOutlined />,
            },
        },
        {
            name: "VectorEditor",
            list: "/admin/vector-editor",
            meta: {
                label: "Vector Editor",
                parent: "DataMenu",
                icon: <CloudServerOutlined />,
            },
        },
        // Customer Management submenu
        {
            name: "CustomerManagementMenu",
            list: "/admin/customer-management",
            meta: {
                label: "Customer Management",
                parent: "AdminMenu",
                icon: <CustomerServiceOutlined />,
            },
        },
        {
            name: "UserActivityReport",
            list: "/admin/user-activity-report",
            meta: {
                label: "User Activity Report",
                parent: "CustomerManagementMenu",
                icon: <FileTextOutlined />,
            },
        },
        {
            name: "OrganizationAdmin",
            list: "/admin/organizations",
            meta: {
                label: "Organization Admin",
                parent: "CustomerManagementMenu",
                icon: <SettingOutlined />,
            },
        },
        {
            name: "TrialUserManagement",
            list: "/admin/trial-users",
            meta: {
                label: "Trial User Management",
                parent: "CustomerManagementMenu",
                icon: <UserOutlined />,
            },
        }
      );
    }

    return baseResources;
  };

  const resources = buildResources();

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider theme={RefineThemes.Blue}>
          <AntdApp>
            <DevtoolsProvider>
              <EmailVerificationControl>
                <AccessControl>
                  <Refine
                    dataProvider={dataProvider(API_BASE_URL)}
                    notificationProvider={useNotificationProvider}
                    routerProvider={routerBindings}
                    authProvider={authProvider}
                    resources={resources}
                    options={{
                      syncWithLocation: true,
                      warnWhenUnsavedChanges: true,
                      useNewQueryKeys: true,
                      projectId: "fVC3FJ-ncQNvO-plwuOB",
                    }}
                  >
                    <Routes>
                      <Route
                        element={
                          <Authenticated
                            key="authenticated-inner"
                            fallback={<CatchAllNavigate to="/login" />}
                          >
                            <ThemedLayoutV2
                              Header={Header}
                              Sider={(props) => (
                                <ThemedSiderV2
                                  {...props}
                                  Title={({ collapsed }) => (
                                    collapsed ? (
                                      <img
                                        src={"/logo-plane-transparent-square-256x256.png"}
                                        alt="CodePilot Logo Collapsed"
                                        style={{ height: 40, margin: '16px 0', display: 'block', maxWidth: '80%' }}
                                      />
                                    ) : (
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '24px 0' }}>
                                        <img
                                          src={"/logo-horiz.png"}
                                          alt="CodePilot Logo"
                                          style={{ height: 56, maxWidth: '90%', display: 'block' }}
                                        />
                                      </div>
                                    )
                                  )}
                                  render={({ dashboard, items /*, logout, collapsed*/ }) => (
                                    <>
                                      {dashboard}
                                      {items}
                                      {/* Logout intentionally omitted from sidebar */}
                                    </>
                                  )}
                                />
                              )}
                            >
                              <Outlet />
                            </ThemedLayoutV2>
                          </Authenticated>
                        }
                      >
                        <Route index
                              element={
                                <NavigateToResource resource="CodeIdentifierUser" />
                              }
                            />

                        <Route path="/code-identifier" element={<CodeIdentifierUser />} />
                        <Route path="/organization/settings" element={<Organization />} />
                        <Route path="/organization/subscription" element={<Subscription />} />
                        <Route path="/organization/facilities" element={<Facilities />} />
                        <Route path="/organization/invoices" element={<Invoices />} />
                        <Route path="/admin/code-identifier" element={<CodeIdentifier />} />
                        <Route path="/admin/prompt-editor" element={<PromptEditor />} />
                        <Route path="/admin/pdpm-prompts" element={<PDPMPromptEditor />} />
                        <Route path="/admin/vector-editor" element={<VectorEditor />} />
                        <Route path="/admin/cache-version" element={<CacheVersion />} />
                        <Route path="/admin/user-activity-report" element={<UserActivityReport />} />
                        <Route path="/admin/organizations" element={<OrganizationAdmin />} />
                        <Route path="/admin/organizations/:organizationId/facilities" element={<Facilities />} />
                        <Route path="/admin/data/icd10-categories" element={<ICD10Categories />} />
                        <Route path="/admin/data/icd10-categories/viewer" element={<ICD10CategoriesViewer />} />
                        <Route path="/admin/data/icd10-nta-points" element={<ICD10NTAPoints />} />
                        <Route path="/admin/data/icd10-nta-points/viewer" element={<ICD10NTAPointsViewer />} />
                        <Route path="/admin/data/icd10-slp" element={<ICD10SLP />} />
                        <Route path="/admin/data/icd10-slp/viewer" element={<ICD10SLPViewer />} />
                        <Route path="/admin/trial-users" element={<TrialUserManagement />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/email-verification" element={<EmailVerification />} />
                       
                        <Route path="*" element={<ErrorComponent />} />
                      </Route>
                      <Route
                        element={
                          <Authenticated
                            key="authenticated-outer"
                            fallback={<Outlet />}
                          >
                            <NavigateToResource />
                          </Authenticated>
                        }
                      >
                        <Route path="/login" element={<Login />} />
                      </Route>
                    </Routes>

                    <RefineKbar />
                    <UnsavedChangesNotifier />
                    <DocumentTitleHandler handler={customTitleHandler} />
                  </Refine>
                </AccessControl>
              </EmailVerificationControl>
            </DevtoolsProvider>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <RoleProvider>
      <OrganizationProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </OrganizationProvider>
    </RoleProvider>
  );
}

export default App;
